<template>
  <div
    v-if="
      !generalStatus?.has_active_coi_on_file &&
      !generalStatus?.has_pending_coi_request
    "
    class="flex flex-col items-center justify-center gap-6 rounded-md border border-steel-300 py-8"
    :class="isTabletOrMobile ? 'h-[600px]' : ''"
  >
    <img
      src="@/assets/file-your-coi.svg"
      alt="File your COI"
    />
    <div
      :class="isTabletOrMobile ? 'text-center' : 'flex flex-col items-center'"
    >
      <h1 class="mb-2 text-xl font-medium text-neutral-boldest">
        File your COI
      </h1>
      <p class="px-2 text-sm font-normal text-neutral-subtle-bold lg:px-0">
        Provide the name of your insurance agency and their contact
        <br v-if="!isTabletOrMobile" />
        information so that we can request your Certificate of Insurance (COI).
      </p>
    </div>
    <div
      :class="[
        'flex w-full gap-3',
        isTabletOrMobile
          ? 'flex-col-reverse px-2'
          : 'flex-row items-center justify-center',
      ]"
    >
      <button
        :class="[
          'rounded-md text-sm font-medium',
          isTabletOrMobile
            ? 'h-12 w-full bg-steel-20 text-neutral-subtle-bold'
            : 'h-10 bg-steel-20 px-3 text-neutral-subtle-bold hover:bg-hovered-gray active:bg-pressed-gray',
        ]"
        @click="onLearnMoreCOI"
      >
        Learn more
      </button>

      <button
        :class="[
          'rounded-md text-sm font-medium',
          isTabletOrMobile
            ? 'h-12 w-full bg-neutral-subtle-yellow text-neutral-boldest'
            : 'h-10 bg-neutral-subtle-yellow px-3 text-neutral-boldest hover:bg-neutral-subtle-yellow-bold active:bg-neutral-subtle-yellow-boldest',
        ]"
        @click="emit('openInsuranceModal')"
      >
        Submit COI Request
      </button>
    </div>
  </div>
  <div
    v-else-if="
      !generalStatus?.has_active_coi_on_file &&
      generalStatus?.has_pending_coi_request
    "
    class="flex flex-col items-center justify-center gap-6 rounded-md border border-steel-300 py-8"
  >
    <img
      src="@/assets/pending-coi-request.svg"
      alt="Pending COI Request"
    />
    <div
      :class="isTabletOrMobile ? 'text-center' : 'flex flex-col items-center'"
    >
      <h1 class="mb-2 text-xl font-medium text-neutral-boldest">
        Pending COI Request
      </h1>
      <p class="text-sm font-normal text-neutral-subtle-bold">
        Please instruct your insurance agency to email your
        <br v-if="!isTabletOrMobile" />
        Certificate of Insurance (COI) to
        <a
          href="mailto:insurance@certs.highway.com"
          class="text-sun-700 underline hover:opacity-90"
        >
          insurance@certs.highway.com
        </a>
      </p>
    </div>
  </div>
  <div
    v-else
    id="certificate"
    class="toc-section"
  >
    <div v-if="activePolicies?.length > 0">
      <section class="flex flex-col text-left">
        <div
          class="flex flex-col items-start justify-start gap-4 lg:gap-0 xl:flex-row"
        >
          <div class="items-left flex w-full flex-col justify-between gap-1">
            <div
              class="relative flex w-full items-center justify-between rounded-xl bg-white p-3 lg:rounded-md"
              style="border: 1px solid rgba(31, 36, 46, 0.14)"
            >
              <div class="flex w-full flex-col items-start justify-start">
                <h3
                  class="text-left text-xs font-medium uppercase text-steel-1100"
                >
                  Certificate of Insurance
                </h3>
                <p class="text-xs font-normal text-steel-700">
                  Received on:
                  {{ formattedActiveDate }}
                </p>
              </div>
              <div class="flex w-full items-center justify-end gap-2">
                <p
                  v-if="!loading"
                  class="mr-2 text-left text-xs text-steel-900"
                >
                  Last updated:
                  {{ dayjs(policyUpdatedAt).format('MM/DD/YY, h:mm a') }}
                </p>
                <ButtonComponent @click.stop="emit('openInsuranceModal')">
                  <span class="text-steel-700">Update Insurance</span>
                </ButtonComponent>
              </div>
            </div>
            <p class="mt-2 text-xs font-normal text-steel-800">
              Here are the specific vehicles covered by your BIPD insurance
              policy. Vehicles absent from the schedule provided on your COI
              will not be insured.
            </p>
          </div>

          <!-- <button
            :class="[
              'rounded-md bg-neutral-subtle-bold px-3 text-sm font-medium text-white hover:bg-neutral-blue active:bg-neutral-boldest',
              isTabletOrMobile ? 'h-12 w-full' : 'h-10',
            ]"
            @click="onOpenUpdateInsurance"
          >
            Update information
          </button> -->
        </div>

        <div>
          <section
            v-if="loading"
            class="grid grid-cols-auto-fill-300 gap-4 py-4"
          >
            <InsuranceCardSkeleton
              v-for="i in 10"
              :key="i"
            />
          </section>
          <section
            v-if="!loading && activePolicies?.length > 0"
            class="grid grid-cols-auto-fill-300 gap-4 py-4"
          >
            <div
              v-for="item in activePolicies"
              :key="item.id"
              class="w-full text-left"
            >
              <insurance-details-card
                v-if="displayInsuranceRedesign"
                :policy="item"
              />
              <insurance-card
                v-else
                :policy="item"
              />
            </div>
          </section>

          <section
            v-else-if="insurance?.fmcsa_active_and_pending_insurance.length > 0"
            class="grid grid-cols-auto-fill-300 gap-4 py-4"
          >
            <div
              v-for="item in insurance.fmcsa_active_and_pending_insurance"
              :key="item.id"
              class="w-full"
            >
              <insurance-government-card-detail
                v-if="displayInsuranceRedesign"
                :insurance="item"
              />
              <insurance-government-card
                v-else
                :insurance="item"
              />
            </div>
          </section>
        </div>
      </section>
    </div>
  </div>

  <!-- <file-insurance-information-modal
    v-if="insuranceModalVisible"
    :visible="insuranceModalVisible"
    @close="onCloseUpdateInsuranceModalVisible"
    @submit="handleUpdateInsuranceModalVisible"
  /> -->
</template>

<script setup>
import { computed, defineProps } from 'vue';
import { useStore } from 'vuex';
import _ from 'lodash';
import { useScreenSize } from '@/composables/useScreenSize';
import InsuranceCard from '@/components/InsuranceCard.vue';
import InsuranceDetailsCard from '@/components/InsuranceDetailsCard.vue';
import InsuranceGovernmentCardDetail from '@/components/InsuranceGovernmentCardDetail.vue';
import InsuranceGovernmentCard from '@/components/InsuranceGovernmentCard.vue';
import InsuranceCardSkeleton from '@/components/InsuranceCardSkeleton.vue';
import { ButtonComponent } from '@/components';
import dayjs from 'dayjs';

const store = useStore();
// const toast = useToast();
const { screenSize } = useScreenSize();

defineProps({
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['openInsuranceModal']);

// const insuranceModalVisible = ref(false);

// const displayInsuranceModal = computed(() => {
//   return store.state.insurance.displayInsuranceModal;
// });

const generalStatus = computed(
  () => store.state.users.userLoadBoardAccess?.user
);

const insurancePolicies = computed(
  () => store.state.insurance?.carrierInsurancePolicies || []
);

const displayInsuranceRedesign = computed(() => {
  return (
    store.state.users.featureFlags?.display_insurance_redesign?.enabled || false
  );
});

const customSort = (a, b) => {
  if (a?.status === 'pending_active' && b?.status !== 'pending_active') {
    return 1;
  }
  if (a?.status !== 'pending_active' && b?.status === 'pending_active') {
    return -1;
  }
  return 0;
};

const insurancePoliciesFiltered = computed(() => {
  const sorted = _.sortBy(insurancePolicies.value, function (element) {
    var rank = {
      commercial_general_liability: 1,
      automobile_liability: 2,
      motor_truck_cargo: 3,
      workers_compensation: 4,
      trailer_interchange: 5,
    };
    return rank[element.is_type];
  });

  return sorted.sort(customSort);
});
const activePolicies = computed(() => {
  return insurancePoliciesFiltered.value.filter(
    (policy) => policy.status !== 'inactive'
  );
});
const formatDate = (date) => {
  return dayjs(date).format('MM/DD/YY');
};
const formattedActiveDate = computed(() => {
  return formatDate(
    activePolicies.value?.[0]?.insurance_certificate?.received_at
  );
});

const policyUpdatedAt = computed(
  () => activePolicies.value?.[0]?.insurance_certificate?.updated_at
);

const isTabletOrMobile = computed(
  () => screenSize.value === 'tablet' || screenSize.value === 'mobile'
);

// function onOpenUpdateInsurance() {
//   insuranceModalVisible.value = true;
// }

function onLearnMoreCOI() {
  window.open(
    `https://service.highway.com/en/collections/10316575-insurance`,
    '_blank'
  );
}

// async function handleUpdateInsuranceModalVisible(agency) {
//   const { name, email, phoneNumber } = agency;

//   const payload = {
//     carrier_id: store.state.users?.currentUser?.company?.carrier_id,
//     contact_email: email,
//     contact_phone: phoneNumber,
//     contact_name: name,
//     company_ids: [1],
//     is_type: 'highway',
//   };

//   try {
//     const res = await store.dispatch(
//       'insurance/insuranceCertificateRequest',
//       payload
//     );

//     if ([200, 201, 204].includes(res.status)) {
//       await store.dispatch('users/checkLoadBoardAccess');
//       toast?.info('Request sent', {
//         position: 'top-right',
//         timeout: 5000,
//       });
//     }
//   } catch (error) {
//     console.error('Error sending insurance request:', error);
//     toast?.error('An error occurred. Please try again.', {
//       position: 'top-right',
//       timeout: 5000,
//     });
//   } finally {
//     onCloseUpdateInsuranceModalVisible();
//   }
// }

// function onCloseUpdateInsuranceModalVisible() {
//   insuranceModalVisible.value = false;
// }

// watch(displayInsuranceModal, (newVal) => {
//   if (newVal) {
//     onOpenUpdateInsurance();
//   }
// });
</script>
